import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";

// ─────────────────────────────────────────────────────────────────────────────
// HENRY-GRIFFITTS COMPONENT COST + MARGIN — second module inside the CFO tab.
// Ported from HG-Cost-Calculator.jsx (standalone). Deliberate deviations only:
//   · the catalog + tariff/adder now PERSIST via /api/cfo/components and
//     /api/cfo/cost-settings (cfo_components / cfo_cost_settings in Mongo) —
//     this is the cost-of-record, not a per-session scratchpad
//   · optimistic UI: edits land on screen instantly, save in the background,
//     with a saved / failed indicator
// The calculation logic (landed cost, margins, loaded cost) is the original.
// ─────────────────────────────────────────────────────────────────────────────

const API = "/api";
const NAVY = "#102134", GREEN = "#48B64C", GOLD = "#966E40";
const MUTE = "#6B7280", INK = "#1A1A1A", RED = "#C0392B";

const fmt = (n) => (n < 0 ? "-$" : "$") + Math.abs(n).toFixed(2);

function headLanded(h, tariff) {
  return (h.unit || 0) + (h.tariffed ? (h.unit || 0) * tariff : 0) + (h.ship || 0) + (h.extra || 0) + (h.ferrule || 0) + (h.hc || 0);
}
function shaftLanded(s, tariff) {
  return (s.unit || 0) + (s.tariffed ? (s.unit || 0) * tariff : 0) + (s.ship || 0) + (s.tag || 0) + (s.pure || 0);
}
function gripLanded(g) {
  return (g.unit || 0) + (g.ship || 0) + (g.tape || 0);
}

const BLANKS = {
  head: { name: "New head", unit: 0, ship: 0, extra: 0, ferrule: 0, hc: 0, tariffed: true, cat: "Iron" },
  shaft: { name: "New shaft", unit: 0, ship: 0, tag: 0.35, pure: 0, tariffed: true },
  grip: { name: "New grip", unit: 0, ship: 0.16, tape: 0.1 },
};
const EDIT_FIELDS = {
  head: ["name", "unit", "ship", "extra", "ferrule", "hc", "tariffed", "cat", "sort_order"],
  shaft: ["name", "unit", "ship", "tag", "pure", "tariffed", "sort_order"],
  grip: ["name", "unit", "ship", "tape", "sort_order"],
};

const rid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID().replace(/-/g, "")
    : Math.random().toString(16).slice(2) + Date.now().toString(16);

export default function CFOCostMargin() {
  const [comps, setComps] = useState({ head: [], shaft: [], grip: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tariff, setTariff] = useState(0.35);
  const [laborAdder, setLaborAdder] = useState("35"); // loaded-cost overhead adder %
  const [headI, setHeadI] = useState(0);
  const [shaftI, setShaftI] = useState(0);
  const [gripI, setGripI] = useState(0);
  const [price, setPrice] = useState("");
  const [tab, setTab] = useState("build");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error

  // Refs so debounced flushes always see the latest values.
  const compsRef = useRef(comps);
  compsRef.current = comps;
  const rowTimers = useRef({});
  const settingsTimer = useRef(null);
  const pendingRef = useRef(0);

  function track(promise) {
    pendingRef.current += 1;
    setSaveState("saving");
    promise
      .then(() => {
        pendingRef.current = Math.max(0, pendingRef.current - 1);
        if (pendingRef.current === 0) {
          setSaveState("saved");
          setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 2500);
        }
      })
      .catch(() => {
        pendingRef.current = Math.max(0, pendingRef.current - 1);
        setSaveState("error");
      });
  }

  // ---- load: catalog + settings from the Hub (seeded server-side on first run)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/cfo/components`);
        const by = { head: [], shaft: [], grip: [] };
        (res.data.components || []).forEach((c) => { if (by[c.type]) by[c.type].push(c); });
        Object.values(by).forEach((a) => a.sort((x, y) => (x.sort_order ?? 0) - (y.sort_order ?? 0)));
        setComps(by);
        const s = res.data.settings || {};
        if (typeof s.tariff_rate === "number") setTariff(s.tariff_rate);
        if (typeof s.labor_adder_pct === "number") setLaborAdder(String(s.labor_adder_pct));
      } catch (e) {
        setLoadError(e?.response?.data?.detail || "Couldn't load the cost catalog.");
      }
      setLoading(false);
    })();
    const timers = rowTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
      if (settingsTimer.current) clearTimeout(settingsTimer.current);
    };
  }, []);

  // ---- persistence helpers (optimistic: state first, save in background) ----
  function flushRow(type, id) {
    const row = (compsRef.current[type] || []).find((r) => r.id === id);
    if (!row) return; // deleted before the debounce fired
    const payload = {};
    EDIT_FIELDS[type].forEach((f) => { if (row[f] !== undefined) payload[f] = row[f]; });
    track(axios.put(`${API}/cfo/components/${id}`, payload));
  }
  function scheduleRowSave(type, id) {
    const key = `${type}:${id}`;
    if (rowTimers.current[key]) clearTimeout(rowTimers.current[key]);
    rowTimers.current[key] = setTimeout(() => {
      delete rowTimers.current[key];
      flushRow(type, id);
    }, 500);
  }
  function scheduleSettingsSave(next) {
    if (settingsTimer.current) clearTimeout(settingsTimer.current);
    settingsTimer.current = setTimeout(() => {
      settingsTimer.current = null;
      track(axios.put(`${API}/cfo/cost-settings`, next));
    }, 500);
  }

  function setTariffPersist(v) {
    setTariff(v);
    scheduleSettingsSave({ tariff_rate: v, labor_adder_pct: parseFloat(laborAdder) || 0 });
  }
  function setLaborAdderPersist(v) {
    setLaborAdder(v);
    scheduleSettingsSave({ tariff_rate: tariff, labor_adder_pct: parseFloat(v) || 0 });
  }

  function updateRow(type, id, key, val) {
    setComps((prev) => ({
      ...prev,
      [type]: prev[type].map((r) => r.id === id ? { ...r, [key]: key === "name" ? val : (parseFloat(val) || 0) } : r),
    }));
    scheduleRowSave(type, id);
  }
  function toggleTariff(type, id) {
    setComps((prev) => ({
      ...prev,
      [type]: prev[type].map((r) => (r.id === id ? { ...r, tariffed: !r.tariffed } : r)),
    }));
    scheduleRowSave(type, id);
  }
  function addRow(type) {
    const list = compsRef.current[type] || [];
    const row = {
      ...BLANKS[type], id: rid(), type,
      sort_order: list.length ? Math.max(...list.map((r) => r.sort_order ?? 0)) + 1 : 0,
    };
    setComps((prev) => ({ ...prev, [type]: [...prev[type], row] }));
    track(axios.post(`${API}/cfo/components`, row));
  }
  function delRow(type, id) {
    const key = `${type}:${id}`;
    if (rowTimers.current[key]) { clearTimeout(rowTimers.current[key]); delete rowTimers.current[key]; }
    setComps((prev) => ({ ...prev, [type]: prev[type].filter((r) => r.id !== id) }));
    track(axios.delete(`${API}/cfo/components/${id}`));
  }

  // ---- derived: the build calculator (original math, untouched) -------------
  const heads = comps.head, shafts = comps.shaft, grips = comps.grip;
  const head = heads[Math.min(headI, Math.max(0, heads.length - 1))];
  const shaft = shafts[Math.min(shaftI, Math.max(0, shafts.length - 1))];
  const grip = grips[Math.min(gripI, Math.max(0, grips.length - 1))];
  const hL = head ? headLanded(head, tariff) : 0;
  const sL = shaft ? shaftLanded(shaft, tariff) : 0;
  const gL = grip ? gripLanded(grip) : 0;
  const componentCost = hL + sL + gL;
  const loaded = componentCost * (1 + (parseFloat(laborAdder) || 0) / 100);

  const p = parseFloat(price);
  const marginComp = !isNaN(p) && p > 0 ? ((p - componentCost) / p) * 100 : null;
  const marginLoaded = !isNaN(p) && p > 0 ? ((p - loaded) / p) * 100 : null;
  const profitComp = !isNaN(p) ? p - componentCost : null;
  const profitLoaded = !isNaN(p) ? p - loaded : null;

  const saveChip = useMemo(() => {
    if (saveState === "saving") return { text: "Saving…", bg: "#F3EFE7", color: MUTE };
    if (saveState === "saved") return { text: "Saved ✓", bg: "#EAF5EA", color: GREEN };
    if (saveState === "error") return { text: "Save failed — retry your last edit", bg: "#FBEAE8", color: RED };
    return null;
  }, [saveState]);

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "22px 20px 70px", fontFamily: "'Inter',system-ui,sans-serif", color: INK }}>
      <style>{`
        .cm-root{box-sizing:border-box;}
        .cm-root *{box-sizing:border-box;}
        .cm-root .fr{font-family:'Fraunces',Georgia,serif;}
        .cm-card{background:#fff;border:1px solid #ECE7DE;border-radius:14px;}
        .cm-btn{cursor:pointer;border:none;border-radius:9px;font-weight:600;font-family:inherit;transition:transform .08s;}
        .cm-btn:active{transform:translateY(1px);}
        .cm-root select,.cm-root input{font-family:inherit;}
        .cm-seg{padding:8px 16px;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;border:1px solid #D9D3C8;background:#fff;color:${NAVY};}
        .cm-seg.on{background:${NAVY};color:#fff;border-color:${NAVY};}
        .cm-brow{display:flex;justify-content:space-between;padding:6px 0;font-size:13.5px;border-bottom:1px solid #F4F0E8;}
        .cm-root input[type=range]{accent-color:${GREEN};}
      `}</style>

      <div className="cm-root">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
          <div>
            <div className="fr" style={{ fontSize: 24, fontWeight: 600, color: NAVY }}>Component Cost & Margin<span style={{ color: GREEN }}>.</span></div>
            <div style={{ color: MUTE, fontSize: 13, marginTop: 2 }}>The cost-of-record — adjust tariff, build a club, see true margin. Every edit saves to the Hub.</div>
          </div>
          {saveChip && (
            <span style={{ fontSize: 12, fontWeight: 600, padding: "4px 11px", borderRadius: 999, background: saveChip.bg, color: saveChip.color }}>{saveChip.text}</span>
          )}
        </div>

        {loading && <div className="cm-card" style={{ padding: 26, color: MUTE, fontSize: 14 }}>Loading the cost catalog…</div>}
        {!loading && loadError && <div className="cm-card" style={{ padding: 26, color: RED, fontSize: 14 }}>{loadError}</div>}

        {!loading && !loadError && (
          <>
            {/* GLOBAL TARIFF — the signature control */}
            <div className="cm-card" style={{ padding: "18px 24px", marginBottom: 18, borderLeft: `4px solid ${GOLD}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div className="fr" style={{ fontSize: 17, fontWeight: 600, color: NAVY }}>Import tariff rate</div>
                  <div style={{ fontSize: 12.5, color: MUTE, marginTop: 2, maxWidth: 460 }}>
                    Applies to all tariffed (imported) components. Your IEEPA situation means this moves — change it here and every landed cost updates. Steel shafts are set as non-tariffed.
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="fr" style={{ fontSize: 34, fontWeight: 600, color: GOLD }}>{(tariff * 100).toFixed(0)}%</div>
                </div>
              </div>
              <input type="range" min="0" max="60" value={tariff * 100} onChange={(e) => setTariffPersist(e.target.value / 100)} style={{ width: "100%", marginTop: 12 }} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {[0, 10, 25, 35, 45].map((v) => (
                  <button key={v} className="cm-btn" onClick={() => setTariffPersist(v / 100)} style={{ background: Math.round(tariff * 100) === v ? GOLD : "#F3EFE7", color: Math.round(tariff * 100) === v ? "#fff" : NAVY, padding: "5px 12px", fontSize: 12 }}>{v}%</button>
                ))}
              </div>
            </div>

            {/* tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <div className={"cm-seg" + (tab === "build" ? " on" : "")} onClick={() => setTab("build")}>Build a club</div>
              <div className={"cm-seg" + (tab === "list" ? " on" : "")} onClick={() => setTab("list")}>All component costs</div>
              <div className={"cm-seg" + (tab === "edit" ? " on" : "")} onClick={() => setTab("edit")}>Edit costs</div>
            </div>

            {tab === "build" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                {/* left: pick parts */}
                <div className="cm-card" style={{ padding: "20px 22px" }}>
                  <div className="fr" style={{ fontSize: 18, fontWeight: 600, color: NAVY, marginBottom: 14 }}>Assemble</div>
                  <Picker label="Head" items={heads} idx={Math.min(headI, Math.max(0, heads.length - 1))} setIdx={setHeadI} landed={(h) => headLanded(h, tariff)} />
                  <Picker label="Shaft" items={shafts} idx={Math.min(shaftI, Math.max(0, shafts.length - 1))} setIdx={setShaftI} landed={(s) => shaftLanded(s, tariff)} />
                  <Picker label="Grip" items={grips} idx={Math.min(gripI, Math.max(0, grips.length - 1))} setIdx={setGripI} landed={gripLanded} />

                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #ECE7DE" }}>
                    <div className="cm-brow"><span>Head landed</span><b>{fmt(hL)}</b></div>
                    <div className="cm-brow"><span>Shaft landed</span><b>{fmt(sL)}</b></div>
                    <div className="cm-brow"><span>Grip landed</span><b>{fmt(gL)}</b></div>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 2px", fontSize: 15 }}>
                      <b style={{ color: NAVY }}>Component cost</b>
                      <b className="fr" style={{ fontSize: 22, color: NAVY }}>{fmt(componentCost)}</b>
                    </div>
                  </div>
                </div>

                {/* right: margin */}
                <div className="cm-card" style={{ padding: "20px 22px" }}>
                  <div className="fr" style={{ fontSize: 18, fontWeight: 600, color: NAVY, marginBottom: 14 }}>Margin</div>

                  <div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Your selling price</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
                    <span style={{ fontSize: 20, color: MUTE }}>$</span>
                    <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00"
                      style={{ flex: 1, padding: "10px 12px", borderRadius: 9, border: "1px solid #D9D3C8", fontSize: 18, fontFamily: "'Fraunces',serif" }} />
                  </div>

                  {marginComp == null ? (
                    <div style={{ color: MUTE, fontSize: 14, padding: "20px 0" }}>Enter a selling price to see margin and profit per club.</div>
                  ) : (
                    <>
                      <MarginBlock label="On component cost" margin={marginComp} profit={profitComp} strong />
                      <div style={{ margin: "14px 0", padding: "12px 14px", background: "#F3F7F3", borderRadius: 10 }}>
                        <div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Loaded cost (adds labor/freight/overhead)</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <input type="range" min="0" max="80" value={laborAdder} onChange={(e) => setLaborAdderPersist(e.target.value)} style={{ flex: 1 }} />
                          <span style={{ fontSize: 13, fontWeight: 600, width: 74 }}>+{laborAdder}% = {fmt(loaded)}</span>
                        </div>
                        <MarginBlock label="On loaded cost (truer)" margin={marginLoaded} profit={profitLoaded} />
                      </div>
                      <div style={{ fontSize: 11.5, color: MUTE, lineHeight: 1.5 }}>
                        Component margin flatters you — it ignores the club builder's time, freight, and overhead. Your real P&L gross margin runs ~52%. The loaded view is the honest one; tune the adder to your actual overhead.
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {tab === "list" && (
              <div className="cm-card" style={{ padding: "18px 22px" }}>
                <ListSection title="Heads" items={heads} landed={(h) => headLanded(h, tariff)} />
                <ListSection title="Shafts" items={shafts} landed={(s) => shaftLanded(s, tariff)} />
                <ListSection title="Grips" items={grips} landed={gripLanded} noTariff />
                <div style={{ fontSize: 11.5, color: MUTE, marginTop: 12, lineHeight: 1.5 }}>
                  Landed cost = unit + tariff (if imported) + shipping + prep (PUREing / ferrule / medallion / headcover / tape). All recompute live from the tariff slider above.
                </div>
              </div>
            )}

            {tab === "edit" && (
              <div>
                <div className="cm-card" style={{ padding: "14px 18px", marginBottom: 14, borderLeft: `4px solid ${GREEN}` }}>
                  <div style={{ fontSize: 13, color: MUTE, lineHeight: 1.5 }}>
                    Edit any unit cost, shipping, or prep number below — landed cost recomputes live and flows into the build calculator. The <b>Tariff?</b> toggle sets whether the global tariff applies (off for domestic steel). Every change saves to the Hub automatically — this table is the cost-of-record.
                  </div>
                </div>
                <EditSection title="Heads" type="head" items={heads} updateRow={updateRow} toggleTariff={toggleTariff} addRow={addRow} delRow={delRow}
                  cols={[["unit", "Unit"], ["ship", "Ship"], ["extra", "Glue/Med"], ["ferrule", "Ferrule"], ["hc", "Headcover"]]}
                  landed={(h) => headLanded(h, tariff)} />
                <EditSection title="Shafts" type="shaft" items={shafts} updateRow={updateRow} toggleTariff={toggleTariff} addRow={addRow} delRow={delRow}
                  cols={[["unit", "Unit"], ["ship", "Ship"], ["tag", "Tag/Epoxy"], ["pure", "PURE"]]}
                  landed={(s) => shaftLanded(s, tariff)} />
                <EditSection title="Grips" type="grip" items={grips} updateRow={updateRow} addRow={addRow} delRow={delRow} noTariff
                  cols={[["unit", "Unit"], ["ship", "Ship"], ["tape", "Tape"]]}
                  landed={gripLanded} />
              </div>
            )}

            <div style={{ marginTop: 18, fontSize: 11.5, color: MUTE, textAlign: "center" }} className="fr">
              Henry-Griffitts · We Deliver Confidence · internal working document
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Picker({ label, items, idx, setIdx, landed }) {
  if (!items.length) return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: MUTE }}>No {label.toLowerCase()}s yet — add one in Edit costs.</div>
    </div>
  );
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select value={idx} onChange={(e) => setIdx(+e.target.value)} style={{ flex: 1, padding: "9px 10px", borderRadius: 8, border: "1px solid #D9D3C8", fontSize: 13.5, background: "#fff" }}>
          {items.map((it, i) => <option key={it.id || i} value={i}>{it.name}</option>)}
        </select>
        <span className="fr" style={{ fontSize: 15, fontWeight: 600, color: GREEN, width: 62, textAlign: "right" }}>{fmt(landed(items[idx]))}</span>
      </div>
    </div>
  );
}

function MarginBlock({ label, margin, profit, strong }) {
  const color = margin >= 50 ? GREEN : margin >= 30 ? GOLD : RED;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <div>
        <div style={{ fontSize: 12, color: MUTE }}>{label}</div>
        <div className="fr" style={{ fontSize: strong ? 32 : 22, fontWeight: 600, color, lineHeight: 1.1 }}>{margin.toFixed(1)}%</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1 }}>profit/club</div>
        <div style={{ fontSize: strong ? 20 : 16, fontWeight: 700, color: profit >= 0 ? NAVY : RED }}>{fmt(profit)}</div>
      </div>
    </div>
  );
}

function EditSection({ title, type, items, updateRow, toggleTariff, addRow, delRow, cols, landed, noTariff }) {
  return (
    <div className="cm-card" style={{ padding: "16px 18px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div className="fr" style={{ fontSize: 17, fontWeight: 600, color: NAVY }}>{title}</div>
        <button className="cm-btn" onClick={() => addRow(type)} style={{ background: GREEN, color: "#fff", padding: "6px 14px", fontSize: 12.5 }}>+ Add {title.slice(0, -1)}</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
          <thead>
            <tr style={{ color: MUTE, textAlign: "left", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <th style={{ padding: "4px 6px", minWidth: 150 }}>Name</th>
              {cols.map(([k, lbl]) => <th key={k} style={{ padding: "4px 6px", textAlign: "right" }}>{lbl}</th>)}
              {!noTariff && <th style={{ padding: "4px 6px", textAlign: "center" }}>Tariff?</th>}
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Landed</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ borderTop: "1px solid #F0EBE2" }}>
                <td style={{ padding: "3px 6px" }}>
                  <input value={it.name} onChange={(e) => updateRow(type, it.id, "name", e.target.value)}
                    style={{ width: "100%", border: "1px solid #E7E2D8", borderRadius: 6, padding: "5px 7px", fontSize: 12.5, fontFamily: "inherit" }} />
                </td>
                {cols.map(([k]) => (
                  <td key={k} style={{ padding: "3px 4px" }}>
                    <input type="number" step="0.01" value={it[k] ?? 0} onChange={(e) => updateRow(type, it.id, k, e.target.value)}
                      style={{ width: 64, border: "1px solid #E7E2D8", borderRadius: 6, padding: "5px 6px", fontSize: 12.5, textAlign: "right", fontFamily: "inherit" }} />
                  </td>
                ))}
                {!noTariff && (
                  <td style={{ padding: "3px 6px", textAlign: "center" }}>
                    <button className="cm-btn" onClick={() => toggleTariff(type, it.id)} title="Toggle whether the global tariff applies"
                      style={{ background: it.tariffed ? GOLD : "#E7E2D8", color: it.tariffed ? "#fff" : MUTE, padding: "3px 9px", fontSize: 11 }}>
                      {it.tariffed ? "on" : "off"}
                    </button>
                  </td>
                )}
                <td style={{ padding: "3px 6px", textAlign: "right" }}>
                  <b className="fr" style={{ color: GREEN }}>{fmt(landed(it))}</b>
                </td>
                <td style={{ padding: "3px 4px", textAlign: "right" }}>
                  <button className="cm-btn" onClick={() => delRow(type, it.id)} style={{ background: "transparent", color: RED, fontSize: 16, padding: "0 4px" }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ListSection({ title, items, landed, noTariff }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="fr" style={{ fontSize: 16, fontWeight: 600, color: NAVY, marginBottom: 8 }}>{title}</div>
      {items.map((it) => (
        <div key={it.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #F4F0E8", fontSize: 13.5 }}>
          <span>{it.name}
            {!noTariff && !it.tariffed && <span style={{ fontSize: 10.5, color: MUTE, marginLeft: 6, background: "#F3EFE7", padding: "1px 6px", borderRadius: 5 }}>no tariff</span>}
          </span>
          <span style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <span style={{ color: MUTE, fontSize: 12 }}>unit {fmt(it.unit || 0)}</span>
            <b className="fr" style={{ color: GREEN, width: 64, textAlign: "right" }}>{fmt(landed(it))}</b>
          </span>
        </div>
      ))}
    </div>
  );
}
